import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { ComingSoonBadge } from '@/components/common/ComingSoonBadge';
import { SectionCard } from '@/components/common/SectionCard';
import { useAuth } from '@/hooks/useAuth';
import { useDataRoomAccess } from '@/hooks/useDataRoomAccess';
import { useIssueReportOffer } from '@/hooks/useIssueReportOffer';
import { ISSUE_REPORT_OFFER_STATE } from '@/lib/constants/issue-report';
import { getPrimaryRole, ROLES } from '@/lib/constants/roles';
import { ROUTES } from '@/lib/constants/routes';
import { formatDate } from '@/lib/utils/date.utils';
import { DATA_ROOM_STRINGS } from '../constants';

const COPY = DATA_ROOM_STRINGS.PAGE.EXPLAINER;

export interface IssueReportExplainerProps {
  /** The acting startup entity. */
  startupId: string;
  /**
   * The startup already holds an Issue Report, open to its buyer only, and the
   * caller is not that buyer: say so, and offer no purchase (FR-138).
   */
  heldByBuyer?: boolean;
}

/**
 * The Data Room page for a startup with no access (`no_active_service`):
 * what the Issue Report is, and the way to get it. A Startup Admin, the only
 * persona who can buy (A-123), gets the purchase page and, when access ran
 * out before, the date it ended. A member is pointed at their Startup
 * Admins. Anyone at a startup whose Issue Report is already open to its buyer
 * is told so, with nothing to buy.
 */
export function IssueReportExplainer({ startupId, heldByBuyer = false }: IssueReportExplainerProps) {
  const { user } = useAuth();
  const canBuy = !heldByBuyer && getPrimaryRole(user?.roles ?? []) === ROLES.STARTUP_ADMIN;
  // The access view is refused to anyone without billing.manage, so it is
  // only asked for on the admin's behalf.
  const access = useDataRoomAccess(startupId, canBuy);
  const lastEnded = access.data?.lastEnded;
  // The buy action follows the offer (FR-203): "Coming Soon" while no plan is on
  // sale, nothing while the offer is loading or failed (so a buy button never
  // flashes and turns into "Coming Soon"). The explainer text around it stays.
  const offer = useIssueReportOffer({ enabled: canBuy });
  const offerState = offer.data?.state;
  let buyAction: ReactNode = null;
  if (offerState === ISSUE_REPORT_OFFER_STATE.COMING_SOON) buyAction = <ComingSoonBadge />;
  if (offerState === ISSUE_REPORT_OFFER_STATE.AVAILABLE) {
    buyAction = (
      <Button asChild variant="yellow">
        <Link to={ROUTES.BILLING_PURCHASE}>{COPY.BUY}</Link>
      </Button>
    );
  }

  return (
    <SectionCard>
      <div className="flex max-w-2xl flex-col gap-2">
        <p className="text-green text-xs font-semibold tracking-wide uppercase">{COPY.eyebrow}</p>
        <h2 className="text-h3 text-navy">{COPY.title}</h2>
        <p className="text-text-secondary text-sm">{COPY.body}</p>
        {heldByBuyer && <p className="text-navy text-sm font-semibold">{COPY.HELD}</p>}
        {lastEnded && (
          <p className="text-text-muted text-sm">{COPY.ENDED.replace('{date}', formatDate(lastEnded.endedAt))}</p>
        )}
        <div className="mt-6">
          {heldByBuyer ? null : canBuy ? (
            buyAction
          ) : (
            <div className="flex flex-col gap-2">
              <p className="text-text-secondary text-sm">{COPY.ASK_ADMIN}</p>
              <Button asChild variant="primary" className="w-fit">
                <Link to={ROUTES.STARTUP_TEAM}>{COPY.SEE_ADMINS}</Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </SectionCard>
  );
}
